import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../common/DashboardLayout';
import httpServices from '../../services/httpServices';
import Swal from 'sweetalert2';
import './CustomTrainingQuiz.css';

const CustomTrainingQuiz = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);

  // Get user info for layout
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = localStorage.getItem('userrole') || '3';
  const userType = user?.type || null;

  useEffect(() => {
    fetchQuiz();
  }, [courseId]);

  const fetchQuiz = async () => {
    try {
      setLoading(true);
      const response = await httpServices.get(`/admin/custom-training/courses/${courseId}/quiz`);

      if (response && response.quiz) {
        setQuiz(response.quiz);
        setCourseTitle(response.courseTitle || 'Custom Training Quiz');
        // Initialize answers array with null values
        setAnswers(new Array(response.quiz.questions.length).fill(null));
      } else {
        throw new Error('Quiz not found');
      }
    } catch (error) {
      console.error('Error fetching quiz:', error);
      // Same as the PDF viewer: a 403 is an audience problem, not a broken quiz.
      const status = error?.response?.status;
      const message =
        status === 403
          ? 'This course is not available to your school. If you think that is wrong, contact your administrator.'
          : status === 401
            ? 'Your session has expired. Please sign in again.'
            : 'Failed to load quiz. Please try again.';
      Swal.fire({
        icon: status === 403 ? 'warning' : 'error',
        title: status === 403 ? 'Not available' : 'Error',
        text: message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (answers.includes(null)) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete',
        text: 'Please answer all questions before submitting.',
      });
      return;
    }

    // Confirm submission
    const confirm = await Swal.fire({
      title: 'Submit Quiz?',
      text: 'Are you sure you want to submit your answers?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Review Answers'
    });

    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      const response = await httpServices.post(
        `/admin/custom-training/quizzes/${quiz.id}/submit`,
        {
          answers,
          courseId
        }
      );

      if (response && response.success) {
        setResults(response);
        setSubmitted(true);

        // Show results
        if (response.passed) {
          Swal.fire({
            icon: 'success',
            title: 'Congratulations!',
            text: `You passed with a score of ${response.score}%!`,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Not Passed',
            text: `You scored ${response.score}%. You need ${quiz.passingScore}% to pass.`,
          });
        }
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to submit quiz. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setAnswers(new Array(quiz.questions.length).fill(null));
    setSubmitted(false);
    setResults(null);
  };

  const handleBackToDashboard = () => {
    navigate('/main-dashboard');
  };

  if (loading) {
    return (
      <DashboardLayout role={userRole} userType={userType} layoutType="topnav">
        <div className="container text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading quiz...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!quiz) {
    return (
      <DashboardLayout role={userRole} userType={userType} layoutType="topnav">
        <div className="container text-center py-5">
          <h3>Quiz Not Found</h3>
          <button className="btn btn-primary mt-3" onClick={handleBackToDashboard}>
            Back to Dashboard
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={userRole} userType={userType} layoutType="topnav">
      <div className="container custom-training-quiz">
        <div className="quiz-header">
          <h2>{courseTitle}</h2>
          <p className="text-muted">{quiz.title}</p>

          {quiz.description && <p>{quiz.description}</p>}
          <div className="quiz-info">
            <span className="badge bg-info">
              {quiz.questions.length} Questions
            </span>
            <span className="badge bg-warning ms-2">
              Passing Score: {quiz.passingScore}%
            </span>
            {quiz.timeLimit && (
              <span className="badge bg-secondary ms-2">
                Time Limit: {quiz.timeLimit} minutes
              </span>
            )}
            <a
              href={`/custom-training/pdf/${courseId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="badge bg-primary ms-2"
              style={{ textDecoration: 'none', cursor: 'pointer', padding: '0.5rem 0.75rem' }}
            >
              📖 View Training Material
            </a>
          </div>
        </div>

        {!submitted ? (
          <div className="quiz-questions">
            {quiz.questions.map((question, qIndex) => {
              // Format question text to add line breaks between scenarios
              const formatQuestionText = (text) => {
                // Split on "Scenario Example" and add line breaks
                const parts = text.split(/(Scenario Example \d+)/i);
                return parts.map((part, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && index % 2 === 1 && <><br /><br /></>}
                    {part}
                  </React.Fragment>
                ));
              };

              return (
                <div key={qIndex} className="question-card card mb-4">
                  <div className="card-body">
                    <h5 className="question-number">Question {qIndex + 1}</h5>
                    <p className="question-text">{formatQuestionText(question.question)}</p>

                    <div className="options">
                      {question.options.map((option, oIndex) => {
                        // Letter labels: A, B, C, D, etc.
                        const letter = String.fromCharCode(65 + oIndex);

                        return (
                          <div key={oIndex} className="form-check option">
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`question-${qIndex}`}
                              id={`q${qIndex}-o${oIndex}`}
                              checked={answers[qIndex] === oIndex}
                              onChange={() => handleAnswerSelect(qIndex, oIndex)}
                            />
                            <label className="form-check-label" htmlFor={`q${qIndex}-o${oIndex}`}>
                              <strong>{letter}.</strong> {option}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="quiz-actions">
              <button
                className="btn btn-secondary me-2"
                onClick={handleBackToDashboard}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Quiz'}
              </button>
            </div>
          </div>
        ) : (
          <div className="quiz-results">
            <div className={`results-card card ${results.passed ? 'border-success' : 'border-danger'}`}>
              <div className="card-body text-center">
                <h3>Quiz Results</h3>
                <div className="score-display">
                  <h1 className={results.passed ? 'text-success' : 'text-danger'}>
                    {results.score}%
                  </h1>
                  <p>{results.passed ? 'Passed!' : 'Not Passed'}</p>
                </div>

                <div className="results-details mt-4">
                  <p>Correct Answers: {results.correctCount} / {quiz.questions.length}</p>
                  <p>Passing Score: {quiz.passingScore}%</p>
                </div>

                <div className="results-actions mt-4">
                  <button
                    className="btn btn-primary me-2"
                    onClick={handleBackToDashboard}
                  >
                    Back to Dashboard
                  </button>
                  {!results.passed && quiz.allowRetakes && (
                    <button
                      className="btn btn-warning"
                      onClick={handleRetake}
                    >
                      Retake Quiz
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Show detailed results */}
            <div className="detailed-results mt-4">
              <h4>Review Answers</h4>
              {results.results.map((result, index) => {
                const userAnswerLetter = String.fromCharCode(65 + result.userAnswer);
                const correctAnswerLetter = String.fromCharCode(65 + result.correctAnswer);

                return (
                  <div key={index} className={`result-item card mb-3 ${result.isCorrect ? 'border-success' : 'border-danger'}`}>
                    <div className="card-body">
                      <h6>Question {index + 1}</h6>
                      <p>{quiz.questions[index].question}</p>
                      <p>
                        <strong>Your Answer:</strong> {userAnswerLetter}. {quiz.questions[index].options[result.userAnswer]}
                        {result.isCorrect ?
                          <span className="text-success ms-2">✓ Correct</span> :
                          <span className="text-danger ms-2">✗ Incorrect</span>
                        }
                      </p>
                      {!result.isCorrect && (
                        <p>
                          <strong>Correct Answer:</strong> {correctAnswerLetter}. {quiz.questions[index].options[result.correctAnswer]}
                        </p>
                      )}
                      {result.explanation && (
                        <p className="text-muted"><em>{result.explanation}</em></p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CustomTrainingQuiz;
